import { useTheme } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { organizationAvailabilityApi } from 'src/api/organization-availability';
import { visitApi } from 'src/api/visit';
import { ICalendarEvent } from 'src/components/calendar/types/calendar';
import { CalendarProfessionalDetailsView } from 'src/components/calendar/view';
import OrganizationCancelVisitModal from 'src/sections/@dashboard/organization/components/OrganizationCancelVisitModal';
import OrganizationVisitModal from 'src/sections/@dashboard/organization/components/OrganizationVisitModal/OrganizationVisitModal';
import TimeOffModal from 'src/sections/@dashboard/visit/components/TimeOffModal';
import {
  GetVisitById,
  GetVisitByIdVisitTypeEnum,
  VisitApiVisitControllerCancelVisitByStaffRequest,
  VisitApiVisitControllerRescheduleVisitByStaffRequest,
} from 'woofwoof-api';
import { useOrganizationContext } from '../../../hooks/useOrganizationContext';
import OrganizationProfessionalCalendarSkeleton from './OrganizationProfessionalCalendarSkeleton';
import { translateKey } from 'src/locales/translate';

// components
// -------------------------------------------------------------------  --

export default function OrganizationProfessionalCalendarContent() {
  const [isRescheduleLoading, setIsRescheduleLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [selectEventId, setSelectEventId] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { professionalId } = useParams<{ professionalId?: string }>();
  const {
    selectedRole,
    selectedOrganization,
    isOrganizationAdminRoleSelected,
  } = useOrganizationContext();

  const { data: timeOffData, refetch: refetchTimeOff } = useQuery(
    ['professional-time-off', professionalId, selectedOrganization?.id],
    () =>
      organizationAvailabilityApi.organizationAvailabilityControllerGetOrganizationAvailability(
        {
          organizationId: selectedOrganization?.id!,
          professionalId: professionalId!,
        },
      ),
    {
      enabled: !!professionalId && !!selectedOrganization?.id,
      retry: 1,
    },
  );

  const onSaveTimeOff = async (timeOffDto: Object[]) => {
    const response =
      await organizationAvailabilityApi.organizationAvailabilityControllerSetOrganizationTimeOff(
        {
          organizationId: selectedOrganization?.id!,
          professionalId: professionalId!,
          setOrganizationTimeOffDto: { timeOff: timeOffDto as any },
        },
      );
    refetchTimeOff();
    return response;
  };
  const theme = useTheme();
  const { data, isLoading, isError, refetch } = useQuery(
    [
      'professional-organization-visits-by-id',
      selectedOrganization?.id,
      professionalId,
      selectedRole,
    ],
    () =>
      visitApi.visitControllerGetVisitsByOrganizationAndProfessionalIdParam({
        organizationId: selectedOrganization?.id!,
        professionalId: professionalId!,
      }),
    {
      retry: 2,
      enabled:
        !!selectedOrganization?.id &&
        isOrganizationAdminRoleSelected &&
        !!professionalId,
      refetchInterval: 30000,
    },
  );

  const { mutateAsync: rescheduleVisitByStaff } = useMutation({
    mutationFn: (
      rescheduleVisitByStaffDto: VisitApiVisitControllerRescheduleVisitByStaffRequest,
    ) =>
      visitApi.visitControllerRescheduleVisitByStaff(rescheduleVisitByStaffDto),
  });

  const { mutateAsync: cancelVisitByStaff } = useMutation({
    mutationFn: (
      cancelVisitByStaffDto: VisitApiVisitControllerCancelVisitByStaffRequest,
    ) => visitApi.visitControllerCancelVisitByStaff(cancelVisitByStaffDto),
  });

  const onCloseVisitModal = useCallback(() => setSelectEventId(''), []);

  const onOpenCancelModal = () => setIsCancelModalOpen(true);

  const onCloseCancelModal = useCallback(() => setIsCancelModalOpen(false), []);

  const onReschedule = useCallback(
    async ({
      professionalId,
      date,
      visitId,
    }: {
      professionalId: string;
      date: string;
      visitId: string;
    }) => {
      try {
        setIsRescheduleLoading(true);
        await rescheduleVisitByStaff({
          visitId,
          rescheduleStaffVisitDto: {
            newStartTime: date,
            newProfessionalId: professionalId,
          },
        });
        await refetch();
        onCloseVisitModal();
        enqueueSnackbar(translateKey("snackbar.visitUpdated"), {
          variant: 'success',
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsRescheduleLoading(false);
      }
    },
    [
      onCloseVisitModal,
      setIsRescheduleLoading,
      rescheduleVisitByStaff,
      enqueueSnackbar,
      refetch,
    ],
  );

  const onCancel = useCallback(async (reason?: string) => {
    try {
      setIsCancelLoading(true);
      await cancelVisitByStaff({
        visitId: selectEventId,
        cancelStaffVisitDto: {
          cancellationReason: reason || translateKey("visit.defaultCancelReasonByStaff"),
        },
      });
      await refetch();
      onCloseCancelModal();
      onCloseVisitModal();
      enqueueSnackbar(translateKey("snackbar.visitCancelled"), {
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsCancelLoading(false);
    }
  }, [
    onCloseCancelModal,
    onCloseVisitModal,
    setIsCancelLoading,
    cancelVisitByStaff,
    enqueueSnackbar,
    refetch,
    selectEventId,
  ]);

  const getEventColor = useCallback(
    (visit: GetVisitById) => {
      const isInThePast = new Date(visit?.endTime) < new Date();

      if (isInThePast) {
        return theme.palette.grey[400];
      }

      if (visit?.visitType === GetVisitByIdVisitTypeEnum.Onsite) {
        return theme.palette.primary.lighter;
      }

      if (visit?.visitType === GetVisitByIdVisitTypeEnum.Video) {
        return theme.palette.warning.lighter;
      }

      if (visit?.visitType === GetVisitByIdVisitTypeEnum.Chat) {
        return theme.palette.error.lighter;
      }

      if (visit?.visitType === GetVisitByIdVisitTypeEnum.Mobile) {
        return theme.palette.success.lighter;
      }

      return theme.palette.primary.main;
    },
    [theme],
  );

  const getEventBorderColor = useCallback(
    (visit: GetVisitById) => {
      const isInThePast = new Date(visit?.endTime) < new Date();

      if (isInThePast) {
        return theme.palette.grey[400];
      }

      if (visit?.visitType === GetVisitByIdVisitTypeEnum.Onsite) {
        return theme.palette.primary.dark;
      }

      if (visit?.visitType === GetVisitByIdVisitTypeEnum.Video) {
        return theme.palette.warning.dark;
      }

      if (visit?.visitType === GetVisitByIdVisitTypeEnum.Chat) {
        return theme.palette.error.dark;
      }

      if (visit?.visitType === GetVisitByIdVisitTypeEnum.Mobile) {
        return theme.palette.success.dark;
      }

      return theme.palette.primary.dark;
    },
    [theme],
  );

  const visitEvents: ICalendarEvent[] = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((visit): ICalendarEvent => {
      return {
        id: visit.id,
        allDay: false,
        title: '',
        start: visit.startTime,
        end: visit.endTime,
        description: '',
        backgroundColor: getEventColor(visit),
        borderColor: getEventBorderColor(visit),
        visit,
      };
    });
  }, [data, getEventColor, getEventBorderColor]);

  const timeOffEvents: ICalendarEvent[] = useMemo(() => {
    const timeOffList = (timeOffData?.data as any)?.timeOff;
    if (!timeOffList?.length) return [];

    return timeOffList.map((off: any): ICalendarEvent => {
      const startDateTime = off.startTime
        ? `${off.startDate.split('T')[0]}T${off.startTime}:00`
        : off.startDate;
      const endDateTime = off.endTime
        ? `${off.endDate.split('T')[0]}T${off.endTime}:00`
        : off.endDate;

      const isInThePast = new Date(endDateTime) < new Date();

      return {
        id: off.id,
        allDay: !off.startTime && !off.endTime,
        title: '',
        start: startDateTime,
        end: endDateTime,
        description: '',
        backgroundColor: isInThePast
          ? theme.palette.grey[400]
          : theme.palette.error.lighter,
        borderColor: isInThePast
          ? theme.palette.grey[400]
          : theme.palette.error.dark,
        timeOff: {
          professionalId: off.professionalId || professionalId || '',
          professionalName: off.professionalName || '',
          ...(off.description && { description: off.description }),
        },
      };
    });
  }, [timeOffData, theme, professionalId]);

  const events: ICalendarEvent[] = useMemo(
    () => [...visitEvents, ...timeOffEvents],
    [visitEvents, timeOffEvents],
  );

  useEffect(() => {
    if (!isLoading && isError && !data) {
      enqueueSnackbar(translateKey("snackbar.visitsLoadFailed"), { variant: 'error' });
    }
  }, [isError, isLoading, data, enqueueSnackbar]);

  return !isLoading ? (
    <>
      <CalendarProfessionalDetailsView
        events={events}
        onEventClick={(arg) => setSelectEventId(arg.event.id)}
        onTimeOff={
          isOrganizationAdminRoleSelected
            ? () => setIsTimeOffModalOpen(true)
            : undefined
        }
      />
      <OrganizationVisitModal
        isOpen={Boolean(selectEventId)}
        onClose={onCloseVisitModal}
        visit={data?.data?.find((visit) => visit.id === selectEventId)}
        onReschedule={onReschedule}
        isRescheduleLoading={isRescheduleLoading}
        onCancel={onOpenCancelModal}
        isCancelLoading={isCancelLoading}
      />
      <OrganizationCancelVisitModal
        isOpen={isCancelModalOpen}
        onClose={onCloseCancelModal}
        onAccept={onCancel}
        isLoading={isCancelLoading}
      />
      <TimeOffModal
        isOpen={isTimeOffModalOpen}
        onClose={() => setIsTimeOffModalOpen(false)}
        timeOff={timeOffData?.data?.timeOff || []}
        refetch={refetchTimeOff}
        onSave={onSaveTimeOff}
        professionalId={professionalId}
        hideExistingPeriods
      />
    </>
  ) : (
    <OrganizationProfessionalCalendarSkeleton />
  );
}
